import { Button } from 'components/atoms';
import { Form } from 'components/organisms';
import { merchInitialValues } from 'components/organisms/forms/merch/MerchForm';
import { FormikHelpers } from 'formik';
import { useMutateOptions } from 'hooks';
import { useParamId } from 'hooks/useParamId';
import middleware from 'middleware';
import { useTranslation } from 'react-i18next';
import { PiX } from 'react-icons/pi';
import { useLocation, useNavigate } from 'react-router-dom';
import { useGetMerch, useUpdateMerch } from 'services/merch';
import { useAddMerchImage, useDeleteMerchImage } from 'services/merch/images';
import { IMerchMutationForm } from 'services/merch/types';
import { onErrorShowAllErrorsToast } from 'utils/service/errorHandler';

const EditMerch = () => {
  const merchId = useParamId('merch');
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const getMerch = useGetMerch(merchId);

  const deleteImage = useDeleteMerchImage(merchId);
  const addImage = useAddMerchImage();

  const backPathname = `/merch/${merchId}/data`;
  const { data: merch } = useGetMerch(merchId);
  const updateMerch = useUpdateMerch();

  const mutateOptions = useMutateOptions<IMerchMutationForm>();

  const onDeleteImage = async (imageId: string) => {
    await deleteImage.mutateAsync(
      { imageId },
      {
        onSuccess: () => getMerch.refetch(),
        onError: error => onErrorShowAllErrorsToast(error),
      }
    );
  };

  const onCreateImage = async (
    croppedFile: File,

    clearAllValues?: () => void
  ) => {
    await addImage.mutateAsync(
      { file: croppedFile, id: merchId, isMain: true },
      {
        onSuccess: async data => {
          clearAllValues && clearAllValues();
        },
        onError: error => onErrorShowAllErrorsToast(error),
      }
    );
  };

  const updateMerchData = async (
    values: IMerchMutationForm,
    helpers: FormikHelpers<IMerchMutationForm>
  ) => {
    const updatedMerch = middleware.merch.formToQuery(values);

    await updateMerch.mutateAsync(
      { id: merchId, ...updatedMerch },
      {
        ...mutateOptions(helpers),
      }
    );
  };

  const onUpdateMerch = async (
    values: IMerchMutationForm,
    helpers: FormikHelpers<IMerchMutationForm>
  ) => {
    try {
      await updateMerchData(values, helpers);

      if (values.gallery.pendingImages?.length) {
        await Promise.all(
          values.gallery.pendingImages.map(async croppedFile => {
            try {
              await onCreateImage(croppedFile);
            } catch (error) {
              console.error('Błąd podczas dodawania obrazu:', error);
            }
          })
        );
      }

      if (values.gallery.deleteImages?.length) {
        await Promise.all(
          values.gallery.deleteImages.map(async imageId => {
            try {
              await onDeleteImage(imageId);
            } catch (error) {
              console.error('Błąd podczas usuwania obrazu:', error);
            }
          })
        );
      }

      navigate(backPathname);
    } catch (error) {
      console.error('Błąd podczas aktualizacji merchu:', error);
    }
  };

  return (
    <Form.Merch
      i18nextPrefix='merch'
      initialValues={
        merch ? middleware.merch.queryToForm(merch) : merchInitialValues
      }
      onSubmit={onUpdateMerch}
      customBackItem={
        <Button
          type='button'
          isClear
          onlyIcon
          onClick={() => navigate(backPathname)}>
          <Button.Icon icon={<PiX />} />
        </Button>
      }
    />
  );
};

export default EditMerch;
